import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Button, TableCell, TableRow } from '@mui/material';
import classnames from 'classnames';

import useToggle from '@hooks/useToggle';
import { removeAdminSupplementInquiryDetails } from '@store/features/adminInquiries/inquiryEntryDetails/slice';
import { handleGetAdminInquiryList } from '@store/features/adminInquiries/inquiryList/actions';
import { adminInquiryListPaginationSelector } from '@store/features/adminInquiries/inquiryList/selectors';
import {
  handleAdminMedicationSendInquiry,
  handleAdminSupplementSendInquiry,
} from '@store/features/adminInquiries/supplementSendInquiry/actions';
import {
  adminSupplementSendInquiryIsLoadingSelector,
  adminSupplementSendInquiryIsSubmittedSelector,
} from '@store/features/adminInquiries/supplementSendInquiry/selectors';
import { removeAdminSupplementSendInquiry } from '@store/features/adminInquiries/supplementSendInquiry/slice';
import useDispatch from '@store/hooks/useDispatch';
import { EventType, useEventBus } from '@utils/eventBus';
import handleToast from '@utils/handleToast';

import ModalConfirmation from './ModalConfirmation/ModalConfirmation';
import { ISendInquiryProps } from './SendInquiry.interface';

import './style.scss';

const SendInquiry: FC<ISendInquiryProps> = ({ className, row }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const eventBus = useEventBus('adminSendInquiry');

  const [isModalOpen, onModalOpen, onModalClose] = useToggle();

  const adminSupplementSendInquiryIsLoading = useSelector(
    adminSupplementSendInquiryIsLoadingSelector,
  );
  const adminSupplementSendInquiryIsSubmitted = useSelector(
    adminSupplementSendInquiryIsSubmittedSelector,
  );
  const adminSearchInquiriesPagination = useSelector(adminInquiryListPaginationSelector);

  const sendInquiry = useCallback(() => {
    if (row.isSupplementary) {
      dispatch(handleAdminSupplementSendInquiry(row.id));
      return;
    }
    dispatch(handleAdminMedicationSendInquiry(row.id));
  }, [dispatch, row]);

  const handleSendInquiryResponseSuccess = useCallback(() => {
    handleToast('success', t('adminInquiries.sendInquiryResponseSuccess'));

    onModalClose();
    dispatch(
      handleGetAdminInquiryList({
        page: adminSearchInquiriesPagination?.page,
        isPending: true,
      }),
    );
    dispatch(removeAdminSupplementInquiryDetails());
    dispatch(removeAdminSupplementSendInquiry());
  }, [onModalClose, dispatch, adminSearchInquiriesPagination?.page]);

  useEffect(() => {
    eventBus.on(EventType.ADMIN_SEND_INQUIRY_SUBMITTED, () => {
      handleSendInquiryResponseSuccess();
    });
    eventBus.on(EventType.ADMIN_SEND_MEDICATION_INQUIRY_SUBMITTED, () => {
      handleSendInquiryResponseSuccess();
    });
    return () => {
      eventBus.off(EventType.ADMIN_SEND_INQUIRY_SUBMITTED);
      eventBus.off(EventType.ADMIN_SEND_MEDICATION_INQUIRY_SUBMITTED);
    };
  }, []);

  return (
    <>
      <TableRow className={classnames('admin-send-inquiry', className)}>
        <TableCell colSpan={5} className="admin-send-inquiry__cell">
          <Button onClick={onModalOpen} variant="contained" className="admin-send-inquiry__button">
            <Box component="img" src="/images/img-submit.svg" />
            <p className="label">{t('adminInquiries.sendInquiry')}</p>
          </Button>
        </TableCell>
      </TableRow>

      <ModalConfirmation
        open={isModalOpen}
        onSubmit={sendInquiry}
        onClose={onModalClose}
        isRightActionDisabled={
          adminSupplementSendInquiryIsLoading || adminSupplementSendInquiryIsSubmitted
        }
      />
    </>
  );
};

export default SendInquiry;
